import { defineStore } from "pinia";
import CryptoJS from "crypto-js";
import { StateTree, _GettersTree, _ActionsTree } from "pinia";

interface AuthState extends StateTree {
  login: string;
  password: string;
  rememberme: boolean;
  data: Record<string, any> | null;
  isLoggedIn: boolean;
  lastInteraction: number | null;
  sessionExpired: boolean;
  unregisteredAffiliateDni: string | null;
  incompleteRegistrationDni: string | null;
}

interface AuthGetters extends _GettersTree<AuthState> {
  getLogin: (state: AuthState) => string;
  getRememberMe: (state: AuthState) => boolean;
  getDecryptedPassword: (state: AuthState) => string;
  getIsLoggedIn: (state: AuthState) => boolean;
  getUserData: (state: AuthState) => Record<string, any> | null;
  getLastInteractionTime: (state: AuthState) => number | null;
  isSessionExpired: (state: AuthState) => boolean;
}

interface AuthActions extends _ActionsTree {
  authenticateUser(payload: {
    login: string;
    password: string;
    rememberme: boolean;
    data: Record<string, any> | null;
  }): void;
  expireSession(payload: { sessionExpired: boolean }): void;
  updateLastInteraction(): boolean;
  updateRememberMe(payload: { rememberme: boolean }): void;
  validateLastInteraction(): boolean;
  logoutUser(): void;
  setUnregisteredAffiliateDni(payload: { dni: string }): void;
  setIncompleteRegistrationDni(payload: { dni: string }): void;
}

export const useAuthStore = defineStore<
  "auth",
  AuthState,
  AuthGetters,
  AuthActions
>("auth", {
  state: () => ({
    login: "",
    password: "",
    rememberme: false,
    data: null,
    isLoggedIn: false,
    lastInteraction: null,
    sessionExpired: true,
    unregisteredAffiliateDni: null,
    incompleteRegistrationDni: null,
  }),

  getters: {
    getLogin: (state) => state.login,
    getRememberMe: (state) => state.rememberme,
    getDecryptedPassword: (state) => {
      const iv = CryptoJS.enc.Hex.parse("0123456789abcdef0123456789abcdef");
      const bytes = CryptoJS.AES.decrypt(state.password, "josejob@", { iv });
      return bytes.toString(CryptoJS.enc.Utf8);
    },
    getIsLoggedIn: (state) => state.isLoggedIn,
    getUserData: (state) => state.data,
    getLastInteractionTime: (state) => state.lastInteraction,
    isSessionExpired: (state) => state.sessionExpired,
  },

  actions: {
    authenticateUser(payload) {
      this.login = payload.login;
      const iv = CryptoJS.enc.Hex.parse("0123456789abcdef0123456789abcdef");
      const encrypted = CryptoJS.AES.encrypt(payload.password, "josejob@", {
        iv,
      }).toString();
      this.password = encrypted;
      this.data = payload.data;
      this.rememberme = payload.rememberme;
      this.isLoggedIn = true;
      this.lastInteraction = Date.now();
    },

    expireSession(payload) {
      this.sessionExpired = payload.sessionExpired;
    },

    updateLastInteraction() {
      return this.validateLastInteraction();
    },

    updateRememberMe(payload) {
      this.rememberme = payload.rememberme;
    },

    validateLastInteraction() {
      const now = Date.now();
      if (this.lastInteraction !== null) {
        const diffInteraction = now - this.lastInteraction;
        const diffInteractionMinutes = diffInteraction / 60000;

        if (diffInteractionMinutes >= 5 && this.sessionExpired === true) {
          this.logoutUser();
          return false;
        }
        this.lastInteraction = Date.now();
      }
      return true;
    },

    logoutUser() {
      if (!this.rememberme) {
        this.login = "";
      }
      this.password = "";
      this.data = null;
      this.isLoggedIn = false;
    },

    setUnregisteredAffiliateDni(payload) {
      this.unregisteredAffiliateDni = payload.dni;
    },
    setIncompleteRegistrationDni(payload) {
      this.incompleteRegistrationDni = payload.dni;
    },
  },

  persist: {
    storage: localStorage,
    pick: [
      "login",
      "password",
      "rememberme",
      "data",
      "isLoggedIn",
      "lastInteraction",
      "sessionExpired",
    ],
  },
});
